import React, { useEffect } from 'react';
import { useUserLocation } from '@/src/hooks';
import useResponsive from '@/src/hooks/useResponsive';
import TLCMobileFilterBar from './TLCMobileFilterBar';
import TLCDesktopFilterBar from './TLCDesktopFilterBar';

type Props = {
  onChange: Function;
  filters: Record<string, any>;
  numberOfSearchResults: number;
};

const GEO_PERMISSION_STATUS = ['timeout', 'reverse_failed', 'rejected'];

const TLCFilterBar = ({ filters, onChange, numberOfSearchResults }: Props) => {
  const { isMobile } = useResponsive();
  const userLocation = useUserLocation({
    shouldRun: !(
      filters?.address || GEO_PERMISSION_STATUS.includes(filters?.geoPermission)
    ),
  });

  useEffect(() => {
    if (
      filters?.address ||
      GEO_PERMISSION_STATUS.includes(filters?.geoPermission)
    ) {
      return;
    }
    const { status, error, location } = userLocation;
    if (status === 'READY' && location) {
      onChange({ address: location });
    } else if (status === 'ERROR' && error) {
      onChange(error);
    }
  }, [userLocation]);

  const selectFilterValue = React.useCallback(
    filter => {
      const { page, ...rest } = filters;
      onChange({ ...rest, ...filter });
    },
    [onChange, filters],
  );

  const resetAll = () => {
    selectFilterValue({
      speciality: undefined,
      extraFeeFilter: undefined,
      newPatientFilter: undefined,
    });
  };

  const numberOfFiltersSelected =
    Number(!!filters.speciality) +
    Number(!!filters.extraFeeFilter) +
    Number(!!filters.newPatientFilter);
  const hasFiltersSelected = numberOfFiltersSelected > 0;

  return isMobile ? (
    <TLCMobileFilterBar
      filters={filters}
      numberOfSearchResults={numberOfSearchResults}
      hasFiltersSelected={hasFiltersSelected}
      numberOfFiltersSelected={numberOfFiltersSelected}
      selectFilterValue={selectFilterValue}
      resetAll={resetAll}
    />
  ) : (
    <TLCDesktopFilterBar
      filters={filters}
      hasFiltersSelected={hasFiltersSelected}
      selectFilterValue={selectFilterValue}
      resetAll={resetAll}
    />
  );
};

export default React.memo(TLCFilterBar);
